"use client"

import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { cn } from "@/lib/utils"
import { cva, type VariantProps } from "class-variance-authority"
import { useBoolean } from "@/hooks"
import { Icon } from "./icon"

const selectTriggerVariants = cva(
  "peer w-full rounded-md text-sm transition-all duration-200 ease-in-out disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default: [
          "border border-border hover:border-action",
          "focus:border-2 focus:border-action focus:outline-none",
          "data-[placeholder]:text-action",
        ],
        primary: [
          "border border-border hover:border-primary/50",
          "focus:border-2 focus:border-primary focus:outline-none",
          "data-[placeholder]:text-muted-foreground",
        ],
        secondary: [
          "border border-border hover:border-secondary/50",
          "focus:border-2 focus:border-secondary focus:outline-none",
          "data-[placeholder]:text-muted-foreground",
        ],
        info: [
          "border border-border hover:border-info/50",
          "focus:border-2 focus:border-info focus:outline-none",
          "data-[placeholder]:text-muted-foreground",
        ],
        success: [
          "border border-border hover:border-success/50",
          "focus:border-2 focus:border-success focus:outline-none",
          "data-[placeholder]:text-muted-foreground",
        ],
        warning: [
          "border border-border hover:border-warning/50",
          "focus:border-2 focus:border-warning focus:outline-none",
          "data-[placeholder]:text-muted-foreground",
        ],
        error: [
          "border border-error hover:border-error",
          "focus:border-2 focus:border-error focus:outline-none",
          "data-[placeholder]:text-error/60",
        ],
      },
      inputSize: {
        default: "h-14",
        sm: "h-11 text-xs",
        lg: "h-16 text-base",
      },
    },
    defaultVariants: {
      variant: "default",
      inputSize: "default",
    },
  }
);

const labelVariants = cva(
  "absolute left-3 origin-[0] transform text-sm transition-all duration-200 ease-in-out",
  {
    variants: {
      variant: {
        default: [
          "text-action",
          "peer-focus:text-action-hover",
          "peer-data-[state=open]:text-action-hover",
          "peer-[:not(:placeholder-shown)]:text-action",
        ],
        primary: [
          "text-action",
          "peer-focus:text-primary",
          "peer-data-[state=open]:text-primary",
          "peer-[:not(:placeholder-shown)]:text-action",
        ],
        secondary: [
          "text-action",
          "peer-focus:text-secondary",
          "peer-data-[state=open]:text-secondary",
          "peer-[:not(:placeholder-shown)]:text-action",
        ],
        info: [
          "text-action",
          "peer-focus:text-info",
          "peer-data-[state=open]:text-info",
          "peer-[:not(:placeholder-shown)]:text-action",
        ],
        success: [
          "text-action",
          "peer-focus:text-success",
          "peer-data-[state=open]:text-success",
          "peer-[:not(:placeholder-shown)]:text-action",
        ],
        warning: [
          "text-action",
          "peer-focus:text-warning",
          "peer-data-[state=open]:text-warning",
          "peer-[:not(:placeholder-shown)]:text-action",
        ],
        error: [
          "text-error",
          "peer-focus:text-error",
          "peer-data-[state=open]:text-error",
          "peer-[:not(:placeholder-shown)]:text-error",
        ],
      },
      inputSize: {
        default: "top-4",
        sm: "top-3",
        lg: "top-5",
      },
    },
    defaultVariants: {
      variant: "default",
      inputSize: "default",
    },
  }
);

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

interface SelectTriggerProps extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>,
  VariantProps<typeof selectTriggerVariants> {
  title?: string;
}

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(({ className, children, title, variant, inputSize, ...props }, ref) => {
  const hasValue = useBoolean();

  React.useEffect(() => {
    // Check if children is not just the placeholder
    if (React.Children.count(children) > 1 || (React.Children.count(children) === 1 && React.isValidElement(children) && children.type === SelectValue)) {
      hasValue.onTrue();
    } else {
      hasValue.onFalse();
    }
  }, [children, hasValue]);

  return (
    <div className="relative w-full">
      <SelectPrimitive.Trigger
        ref={ref}
        className={cn(
          selectTriggerVariants({ variant, inputSize }),
          "flex items-center justify-between px-3 py-2 bg-white",
          "[&>span]:max-h-[80px] [&>span]:overflow-y-auto [&>span]:scrollbar-thin [&>span]:scrollbar-track-transparent [&>span]:scrollbar-thumb-action/20 hover:[&>span]:scrollbar-thumb-action/40",
          className
        )}
        {...props}
      >
        {children}
        <SelectPrimitive.Icon asChild>
          <Icon icon="material-symbols:expand-more" size={'xs'} />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <label
        className={cn(
          labelVariants({ variant, inputSize }),
          hasValue.value && "-translate-y-5 scale-75 bg-white px-1",
          "peer-focus:-translate-y-5 peer-focus:scale-75 peer-focus:bg-white peer-focus:px-1",
          "peer-data-[state=open]:-translate-y-5 peer-data-[state=open]:scale-75 peer-data-[state=open]:bg-white peer-data-[state=open]:px-1"
        )}
      >
        {title}
      </label>
    </div>
  )
})
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}
  >
    <Icon icon="material-symbols:chevron-up" size={24} />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}
  >
    <Icon icon="material-symbols:chevron-down" size={24} />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "p-1",
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("py-1.5 pl-8 pr-2 text-sm font-semibold", className)}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-primary/5 hover:bg-primary/5 focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Icon icon="material-symbols:check" size={24} />
      </SelectPrimitive.ItemIndicator>
    </span>

    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}
